import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { post } from 'common/src/vtj/browser/http'
import { Serialized } from 'common/src/vtj/types/util.type'
import { runInAction, toJS } from 'mobx'
import { useMemo } from 'react'
import { TextInputValue } from 'suomifi-ui-components'
import { isValueInEnum } from 'todistukset-common/src/vtj/type-guards'
import {
  AetTodistusKieli,
  EditableField,
  editIlmoitettuArvo,
  finalEditableFieldValue,
  KirkollinenVihkiminen,
  SailyykoSukunimi,
  UusiSukunimi,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import { AetOrdererUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { PlaceOfMarriage } from 'todistukset-common/src/vtj/types/place-of-marriage'
import { TodistusPyyntöAet } from 'todistukset-common/src/vtj/types/todistus'
import { TodistusOrder } from 'todistukset-common/src/vtj/types/todistus-order.type'
import RequestStatusEnum from 'todistukset-ui/src/vtj/RequestStatus.enum'
import {
  OnClickParams,
  OnNextClick,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import aetSharedSetters from 'todistukset-ui/src/vtj/stores/actions/aet-shared-setters'
import {
  handleError,
  setOrderRequestStatus,
} from 'todistukset-ui/src/vtj/stores/actions/common-actions'
import { refreshSession } from 'todistukset-ui/src/vtj/stores/actions/session-timer-actions'
import {
  AetLastnameSelection,
  useAetOrdererForm,
} from 'todistukset-ui/src/vtj/stores/aet-orderer-form.store'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'

type Form = AetOrdererUiForm
type Setters = typeof setters
export type AetOrdererFormActions = {
  [Prop in keyof Setters]: Setters[Prop] extends (
    form: Form,
    ...args: infer Args
  ) => void
    ? (...args: Args) => void
    : never
}

export function useAetOrdererFormActions(): AetOrdererFormActions {
  const form = useAetOrdererForm()
  return useMemo(() => getAetOrdererFormActions(form), [form])
}

export function getAetOrdererFormActions(form: Form): AetOrdererFormActions {
  return (Object.keys(setters) as (keyof Setters)[]).reduce(
    (actions, fnName) => {
      actions[fnName] = (...args: unknown[]) => {
        runInAction(() => {
          refreshSession()
          const setter = setters[fnName] as (
            form: Form,
            ...args: unknown[]
          ) => void
          setter(form, ...args)
        })
      }
      return actions
    },
    {} as AetOrdererFormActions
  )
}

const setters = {
  ...aetSharedSetters,
  setOrdererKeepingCurrentLastname,
  setOrdererNewLastName,
  setSpouseKeepingCurrentLastname,
  setSpouseNewLastName,
  setSpouseHetu,
  setSpouseEmail,
  setPlaceOfMarriage,
  setTodistusLanguage,
  setIsKirkollinenVihkiminen,
}

function setOrdererKeepingCurrentLastname(form: Form, value: string) {
  form.keepingCurrentLastName = editIlmoitettuArvo<SailyykoSukunimi>(
    null,
    isValueInEnum(value, AetLastnameSelection)
      ? value === AetLastnameSelection.CURRENT_NAME
      : null
  )

  if (finalEditableFieldValue(form.keepingCurrentLastName)) {
    setOrdererNewLastName(form, undefined)
  }
}

function setOrdererNewLastName(form: Form, value: TextInputValue) {
  form.newLastName = editIlmoitettuArvo<UusiSukunimi>(
    null,
    typeof value === 'string' ? value : null
  )
}

function setSpouseKeepingCurrentLastname(form: Form, value: string) {
  form.spouseKeepingCurrentLastName = editIlmoitettuArvo<SailyykoSukunimi>(
    null,
    isValueInEnum(value, AetLastnameSelection)
      ? value === AetLastnameSelection.CURRENT_NAME
      : null
  )

  if (finalEditableFieldValue(form.spouseKeepingCurrentLastName)) {
    setSpouseNewLastName(form, undefined)
  }
}

function setSpouseNewLastName(form: Form, value: TextInputValue) {
  form.spouseNewLastName = editIlmoitettuArvo<UusiSukunimi>(
    null,
    typeof value === 'string' ? value : null
  )
}

function setSpouseHetu(form: Form, value: TextInputValue) {
  form.spouseHetu = typeof value === 'string' ? value : ''
}

function setSpouseEmail(form: Form, value: TextInputValue) {
  form.spouseEmail = typeof value === 'string' ? value : ''
}

function setPlaceOfMarriage(form: Form, value: string) {
  form.placeOfMarriage = editIlmoitettuArvo<EditableField<PlaceOfMarriage>>(
    null,
    isValueInEnum(value, PlaceOfMarriage) ? value : null
  )
}

function setTodistusLanguage(form: Form, value: string) {
  enum FiAndSv {
    FI = LanguageCode.FI,
    SV = LanguageCode.SV,
  }

  form.todistusLanguage = editIlmoitettuArvo<AetTodistusKieli>(
    null,
    isValueInEnum(value, FiAndSv)
      ? (value as unknown as LanguageCode.FI | LanguageCode.SV)
      : null
  )
}

function setIsKirkollinenVihkiminen(
  form: Form,
  value: { checkboxState: boolean }
) {
  form.isKirkollinenVihkiminen = editIlmoitettuArvo<KirkollinenVihkiminen>(
    null,
    value.checkboxState
  )
}

export const useSubmitAetOrdererForm: () => OnNextClick = () => {
  const { order, aetStore } = useStore()
  const form = useAetOrdererForm()

  return async ({ onSuccess }: OnClickParams): Promise<void> => {
    if (order.requestStatus === RequestStatusEnum.SENDING) {
      return
    }

    setOrderRequestStatus({ order, requestStatus: RequestStatusEnum.SENDING })

    try {
      const [error] = await post<Serialized<TodistusOrder<TodistusPyyntöAet>>>(
        '/api/restricted/todistukset/aet/new',
        toJS(form)
      )

      if (error) {
        throw error
      }

      aetStore.loadAetTodistuspyynnot()

      setOrderRequestStatus({ order, requestStatus: RequestStatusEnum.SENT })
      onSuccess()
    } catch (error) {
      const storePath = 'order.requestStatus'
      const errorValue = RequestStatusEnum.ERROR
      handleError({ error, storePath, errorValue })
    }
  }
}
